/**
 * AiMemoryCard — the user's window into everything Fias AI remembers
 * (the ChatGPT memory-management contract): the master switch, the three
 * layers (instructions / when-I-say-X rules / learned), per-entry edit,
 * enable/disable, delete, and delete-all. Mounted on Account Settings;
 * the assistant window's gear links here.
 */

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Brain, Plus, Trash2, Pencil, Check, X } from 'lucide-react';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';

type MemoryKind = 'instruction' | 'rule' | 'learned';

interface MemoryRow {
  memoryId: string;
  kind: MemoryKind;
  title: string | null;
  content: string;
  source: 'user' | 'assistant';
  enabled: boolean;
}

const KIND_META: Record<MemoryKind, { heading: string; hint: string; addLabel: string }> = {
  instruction: {
    heading: 'Instructions',
    hint: 'Standing guidance Fias AI follows in every conversation.',
    addLabel: 'Add instruction',
  },
  rule: {
    heading: 'Rules',
    hint: '“When I say X, do Y” shorthands (e.g. “EOD means 6pm”).',
    addLabel: 'Add rule',
  },
  learned: {
    heading: 'Learned by Fias AI',
    hint: 'Saved during conversations — always visible here, never silent.',
    addLabel: 'Add memory',
  },
};

export function AiMemoryCard() {
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: queryKeys.fiasAI.memory(),
    queryFn: () => api.getAssistantMemory(),
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: queryKeys.fiasAI.memory() });

  const setEnabled = useMutation({
    mutationFn: (enabled: boolean) => api.setAssistantMemoryEnabled(enabled),
    onSuccess: invalidate,
  });
  const createMemory = useMutation({
    mutationFn: (input: { kind: MemoryKind; content: string }) => api.createAssistantMemory(input),
    onSuccess: invalidate,
  });
  const updateMemory = useMutation({
    mutationFn: (input: { memoryId: string; content?: string; enabled?: boolean }) =>
      api.updateAssistantMemory(input.memoryId, {
        content: input.content,
        enabled: input.enabled,
      }),
    onSuccess: invalidate,
  });
  const deleteMemory = useMutation({
    mutationFn: (memoryId: string) => api.deleteAssistantMemory(memoryId),
    onSuccess: invalidate,
  });
  const deleteAll = useMutation({
    mutationFn: () => api.deleteAllAssistantMemories(),
    onSuccess: invalidate,
  });

  const memories = (data?.memories ?? []) as MemoryRow[];
  const enabled = data?.enabled ?? true;

  return (
    <Card data-testid="ai-memory-card">
      <CardHeader>
        <div className="flex items-center justify-between gap-4">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Brain className="h-5 w-5" /> Fias AI Memory
            </CardTitle>
            <CardDescription>
              Everything Fias AI remembers about you — yours to edit, disable, or delete. Memory
              never changes safety rules: risky actions always ask you first.
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Memory</span>
            <Switch
              checked={enabled}
              onCheckedChange={(v) => setEnabled.mutate(v)}
              aria-label="Memory master switch"
            />
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {isLoading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : (
          <>
            {!enabled && (
              <p className="text-sm text-muted-foreground rounded-md bg-muted px-3 py-2">
                Memory is off — Fias AI won’t use or save memories, but your entries are kept and
                you can still manage them below.
              </p>
            )}
            {(Object.keys(KIND_META) as MemoryKind[]).map((kind) => (
              <MemorySection
                key={kind}
                kind={kind}
                memories={memories.filter((m) => m.kind === kind)}
                onAdd={(content) => createMemory.mutate({ kind, content })}
                onEdit={(memoryId, content) => updateMemory.mutate({ memoryId, content })}
                onToggle={(memoryId, on) => updateMemory.mutate({ memoryId, enabled: on })}
                onDelete={(memoryId) => deleteMemory.mutate(memoryId)}
              />
            ))}
            {memories.length > 0 && (
              <div className="pt-2 border-t border-border">
                <Button
                  variant="outline"
                  size="sm"
                  className="text-destructive"
                  onClick={() => deleteAll.mutate()}
                  disabled={deleteAll.isPending}
                >
                  <Trash2 className="h-4 w-4 mr-1.5" /> Delete all memory
                </Button>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

function MemorySection({
  kind,
  memories,
  onAdd,
  onEdit,
  onToggle,
  onDelete,
}: {
  kind: MemoryKind;
  memories: MemoryRow[];
  onAdd: (content: string) => void;
  onEdit: (memoryId: string, content: string) => void;
  onToggle: (memoryId: string, enabled: boolean) => void;
  onDelete: (memoryId: string) => void;
}) {
  const meta = KIND_META[kind];
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState('');

  return (
    <div>
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold">{meta.heading}</h3>
          <p className="text-xs text-muted-foreground">{meta.hint}</p>
        </div>
        <Button variant="ghost" size="sm" onClick={() => setAdding(true)}>
          <Plus className="h-4 w-4 mr-1" /> {meta.addLabel}
        </Button>
      </div>

      {adding && (
        <div className="mt-2 space-y-2">
          <Textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            maxLength={2000}
            rows={2}
            placeholder={kind === 'rule' ? 'When I say …, do …' : 'What should Fias AI remember?'}
          />
          <div className="flex gap-2">
            <Button
              size="sm"
              onClick={() => {
                if (draft.trim()) onAdd(draft.trim());
                setDraft('');
                setAdding(false);
              }}
            >
              <Check className="h-4 w-4 mr-1" /> Save
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setAdding(false)}>
              Cancel
            </Button>
          </div>
        </div>
      )}

      <ul className="mt-2 space-y-1.5">
        {memories.length === 0 && !adding && (
          <li className="text-xs text-muted-foreground italic">Nothing here yet.</li>
        )}
        {memories.map((m) => (
          <li
            key={m.memoryId}
            className="flex items-start gap-2 rounded-md border border-border px-3 py-2 text-sm"
          >
            {editingId === m.memoryId ? (
              <div className="flex-1 space-y-2">
                <Textarea
                  value={editDraft}
                  onChange={(e) => setEditDraft(e.target.value)}
                  maxLength={2000}
                  rows={2}
                />
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    onClick={() => {
                      if (editDraft.trim()) onEdit(m.memoryId, editDraft.trim());
                      setEditingId(null);
                    }}
                  >
                    <Check className="h-4 w-4 mr-1" /> Save
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setEditingId(null)}>
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <>
                <span
                  className={`flex-1 min-w-0 break-words ${m.enabled ? '' : 'line-through text-muted-foreground'}`}
                >
                  {m.title ? <span className="font-medium">{m.title}: </span> : null}
                  {m.content}
                </span>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <Switch
                    checked={m.enabled}
                    onCheckedChange={(v) => onToggle(m.memoryId, v)}
                    aria-label="Enable memory entry"
                  />
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7"
                    onClick={() => {
                      setEditingId(m.memoryId);
                      setEditDraft(m.content);
                    }}
                    title="Edit"
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 text-destructive"
                    onClick={() => onDelete(m.memoryId)}
                    title="Delete"
                  >
                    <X className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
